import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DIAL_CODES } from "@/components/dashboard/PhoneCard";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2, Circle, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import client from "@/api/client";
import { SEO } from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";

const LS_KEY = "sug_journey_draft_v1";
const LS_SID = "sug_journey_session_v1";
const FLYER_APPEAR_MS = 40000; // 40 seconds after page load
const FLYER_AUTOCLOSE_MS = 30000; // 30 seconds after flyer becomes visible

function getSessionId() {
  try {
    let sid = localStorage.getItem(LS_SID);
    if (!sid) {
      sid = `js_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(LS_SID, sid);
    }
    return sid;
  } catch (_) { return `js_${Date.now().toString(36)}`; }
}

// Same Arabic-plural helper as JourneyHero — see comment there for logic.
function stepsLabel(count, isAr) {
  if (!isAr) return count === 1 ? "step" : "steps";
  const n = Math.abs(count);
  if (n === 1) return "خطوة";
  if (n === 2) return "خطوتان";
  if (n >= 3 && n <= 10) return "خطوات";
  return "خطوة";
}

/**
 * JourneyPage — post-hero flow. Shows the programs for the picked
 * business type, lets the visitor toggle which programs they want,
 * and converts with an email-only signup that surfaces as a floating
 * flyer 40s in (auto-dismissing after 30s of visibility). Draft is
 * persisted in localStorage so a mid-flow reload keeps their choices.
 */
export default function JourneyPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const typeSlug = params.get("type") || "";
  const { setUser, user: authUser } = useAuth();

  // If the visitor is already signed in, bypass the funnel and drop them
  // straight on their dashboard — respects both approved and pending users.
  useEffect(() => {
    if (authUser) navigate("/dashboard", { replace: true });
  }, [authUser, navigate]);

  // Anonymous funnel session — allows admins to see drop-off analytics.
  useEffect(() => {
    if (authUser || !typeSlug) return;
    const sid = getSessionId();
    client.post("/journey/track", { session_id: sid, business_type_slug: typeSlug, event: "view" }).catch(() => {});
  }, [authUser, typeSlug]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d?.business_type_slug === (new URLSearchParams(window.location.search).get("type") || "") && Array.isArray(d?.selected_template_ids)) {
          return new Set(d.selected_template_ids);
        }
      }
    } catch (_) { /* ignore */ }
    return null;
  });
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("966");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [flyerOpen, setFlyerOpen] = useState(false);
  const [flyerDismissed, setFlyerDismissed] = useState(false);
  const autoCloseRef = useRef(null);

  const load = useCallback(async () => {
    if (!typeSlug) {
      navigate("/");
      return;
    }
    setLoading(true);
    try {
      const res = await client.get("/journey/preview", { params: { type_slug: typeSlug } });
      setData(res.data);
      setSelected((prev) => {
        if (prev !== null) return prev;
        return new Set((res.data?.programs || []).map((p) => p.template_id));
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? "تعذّر تحميل الرحلة" : "Could not load journey"));
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [typeSlug, isAr, navigate]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!typeSlug || selected === null) return;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({
        business_type_slug: typeSlug,
        selected_template_ids: Array.from(selected),
      }));
    } catch (_) { /* ignore */ }
  }, [selected, typeSlug]);

  // Flyer scheduler — appear 40s after load, auto-close 30s after appearance.
  useEffect(() => {
    if (flyerDismissed || loading || !data) return;
    const appearTimer = setTimeout(() => setFlyerOpen(true), FLYER_APPEAR_MS);
    return () => clearTimeout(appearTimer);
  }, [flyerDismissed, loading, data]);

  useEffect(() => {
    if (!flyerOpen) return;
    autoCloseRef.current = setTimeout(() => {
      setFlyerOpen(false);
      setFlyerDismissed(true);
    }, FLYER_AUTOCLOSE_MS);
    return () => clearTimeout(autoCloseRef.current);
  }, [flyerOpen]);

  const closeFlyer = () => {
    setFlyerOpen(false);
    setFlyerDismissed(true);
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
  };

  const toggle = (tid) => {
    setSelected((prev) => {
      const base = prev instanceof Set ? prev : new Set();
      const next = new Set(base);
      next.has(tid) ? next.delete(tid) : next.add(tid);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(isAr ? "أدخل بريدك الإلكتروني للمتابعة" : "Enter your email to continue");
      return;
    }
    setSubmitting(true);
    try {
      const res = await client.post("/auth/journey-signup", {
        email: email.trim(),
        full_name: fullName.trim(),
        business_type_slug: typeSlug,
        selected_template_ids: selected instanceof Set ? Array.from(selected) : [],
        session_id: getSessionId(),
        phone_country: phoneCountry,
        phone: phone.trim(),
      });
      if (setUser && res.data?.user) setUser(res.data.user);
      try { localStorage.removeItem(LS_KEY); } catch (_) { /* ignore */ }
      toast.success(isAr ? "تم إنشاء حسابك. بانتظار موافقة المشرف." : "Account created — awaiting approval.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isAr ? "فشل التسجيل" : "Signup failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const stepCount = useMemo(() => {
    if (!(selected instanceof Set)) return 0;
    return (data?.programs || []).filter((p) => selected.has(p.template_id))
      .reduce((sum, p) => sum + (p.milestones?.length || 0), 0);
  }, [data, selected]);

  if (loading || !data) {
    return (
      <div className="flex justify-center py-16 sm:py-24">
        {/* Bare /journey redirects home, so claim the noindex head here too. */}
        <SEO title="Journey" description="Pick your business type to see a tailored plan." path="/journey" noindex />
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const bt = data.business_type;
  const btLabel = isAr ? (bt.name_ar || bt.name_en) : bt.name_en;

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="journey-page">
      <SEO
        title={`${btLabel} — Journey`}
        description={bt.tagline_en || ""}
        path="/journey"
        noindex
      />

      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate("/")}
        className="rounded-xl gap-2 -ms-3 mb-3"
        data-testid="journey-back"
      >
        <ArrowLeft className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} />
        {isAr ? "رجوع" : "Back"}
      </Button>

      {/* Save-your-journey CTA — always at the top so visitors can convert
          before scrolling through the program list. */}
      <div className="mb-6 sm:mb-8 flex justify-center sm:justify-end">
        <Button
          className="btn-glossy relative overflow-hidden rounded-2xl gap-2 text-sm sm:text-base h-12 sm:h-14 px-6 sm:px-8 group w-full sm:w-auto"
          onClick={() => { setFlyerDismissed(false); setFlyerOpen(true); }}
          data-testid="journey-open-signup-top"
        >
          <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent animate-[shimmer_2.8s_ease-in-out_infinite]" />
          <span className="relative z-10 flex items-center gap-2 justify-center">
            <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
            {isAr ? "احفظ رحلتي" : "Save my journey"}
            <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 ${isAr ? "rotate-180 group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5"}`} />
          </span>
        </Button>
      </div>

      <div className="mb-8 sm:mb-10">
        <p className="text-xs sm:text-sm uppercase tracking-widest text-primary font-semibold">
          {isAr ? "رحلتك في المنصة" : "Your platform journey"}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mt-2 leading-tight">{btLabel}</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-2 max-w-2xl">
          {isAr ? (bt.tagline_ar || bt.tagline_en) : bt.tagline_en}
        </p>
      </div>

      {/* Programs list */}
      <div className="space-y-3 sm:space-y-4 mb-6" data-testid="journey-programs">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <h2 className="font-display text-base sm:text-lg font-semibold">
            {isAr ? "البرامج المقترحة" : "Recommended programs"}
          </h2>
          <span className="text-[11px] sm:text-xs text-muted-foreground">
            {(selected instanceof Set ? selected.size : 0)} / {data.programs.length} · {stepCount} {stepsLabel(stepCount, isAr)}
          </span>
        </div>
        {data.programs.map((p) => {
          const on = selected instanceof Set && selected.has(p.template_id);
          return (
            <Card
              key={p.template_id}
              className={`rounded-2xl border p-4 sm:p-5 transition-all ${on ? "border-primary bg-primary/5" : "border-border"}`}
              data-testid={`journey-program-${p.template_id}`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={selected instanceof Set && selected.has(p.template_id)}
                  onCheckedChange={() => toggle(p.template_id)}
                  className="mt-1"
                  data-testid={`journey-program-toggle-${p.template_id}`}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm">{isAr ? (p.name_ar || p.name) : p.name}</p>
                  <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5">
                    {isAr ? (p.category_ar || p.category) : p.category}{(isAr ? (p.focus_ar || p.focus) : p.focus) ? ` · ${isAr ? (p.focus_ar || p.focus) : p.focus}` : ""}
                  </p>
                  <ol className="mt-2.5 space-y-1">
                    {(p.milestones || []).map((m, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] sm:text-xs text-muted-foreground">
                        {on ? <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0 mt-0.5" /> : <Circle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
                        <span>{isAr ? (m.title_ar || m.title) : m.title}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Persistent inline CTA (bottom of program list) — same visual weight as the top CTA. */}
      <div className="mt-8 flex justify-center">
        <Button
          className="btn-glossy relative overflow-hidden rounded-2xl gap-2 text-sm sm:text-base h-12 sm:h-14 px-6 sm:px-8 group w-full sm:w-auto"
          onClick={() => { setFlyerDismissed(false); setFlyerOpen(true); }}
          data-testid="journey-open-signup"
        >
          <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent animate-[shimmer_2.8s_ease-in-out_infinite]" />
          <span className="relative z-10 flex items-center gap-2 justify-center">
            <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
            {isAr ? "احفظ رحلتي" : "Save my journey"}
            <ArrowRight className={`h-4 w-4 sm:h-5 sm:w-5 transition-transform duration-200 ${isAr ? "rotate-180 group-hover:-translate-x-0.5" : "group-hover:translate-x-0.5"}`} />
          </span>
        </Button>
      </div>

      {/* Centered save-your-journey flyer — appears after 40s, auto-closes after 30s */}
      <AnimatePresence>
        {flyerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={closeFlyer}
            data-testid="journey-flyer-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.96 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="journey-flyer"
            >
              <Card className="rounded-2xl border border-primary/40 bg-background shadow-2xl p-5 sm:p-6 relative">
                <button
                  type="button"
                  onClick={closeFlyer}
                  aria-label={isAr ? "إغلاق" : "Close"}
                  className="absolute top-3 end-3 rounded-lg p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  data-testid="journey-flyer-close"
                >
                  <X className="h-4 w-4" />
                </button>
                <div className="flex items-start gap-3 pe-8">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary flex-shrink-0">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-display text-lg sm:text-xl font-semibold">
                      {isAr ? "احفظ رحلتك بالبريد فقط" : "Save your journey — email only"}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                      {isAr ? "بدون كلمة مرور. سيراجع المشرف حسابك خلال ٢٤ ساعة." : "No password. Admin approval within 24h."}
                    </p>
                  </div>
                </div>
                <form onSubmit={submit} className="mt-4 space-y-3">
                  <div>
                    <Label className="text-xs">{isAr ? "الاسم (اختياري)" : "Full name (optional)"}</Label>
                    <Input
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="rounded-xl mt-1 h-10 text-sm"
                      placeholder={isAr ? "اسمك الكامل" : "Your full name"}
                      data-testid="journey-signup-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? "رقم الجوال (اختياري)" : "Phone number (optional)"}</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={phoneCountry} onValueChange={setPhoneCountry}>
                        <SelectTrigger className="rounded-xl h-10 w-[120px] text-sm" data-testid="journey-signup-phone-country">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {DIAL_CODES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/[^0-9\s-]/g, ""))}
                        className="rounded-xl h-10 text-sm flex-1 font-mono"
                        placeholder="5X XXX XXXX"
                        dir="ltr"
                        inputMode="tel"
                        data-testid="journey-signup-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">{isAr ? "البريد الإلكتروني" : "Email"} <span className="text-primary">*</span></Label>
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl mt-1 h-10 text-sm"
                      placeholder="you@example.com"
                      data-testid="journey-signup-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="btn-glossy relative overflow-hidden rounded-xl w-full gap-2 mt-2 h-11 text-sm font-semibold"
                    disabled={submitting || !email.trim()}
                    data-testid="journey-signup-submit"
                  >
                    <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent animate-[shimmer_2.8s_ease-in-out_infinite]" />
                    <span className="relative z-10 flex items-center gap-2">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className={`h-4 w-4 ${isAr ? "rotate-180" : ""}`} />}
                      {isAr ? "إنشاء الحساب" : "Create account"}
                    </span>
                  </Button>
                </form>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
